import type { RewardRegistrationIntent } from '~/utils/rewardRegistration'
import { parseRewardRegistration, REWARD_REGISTRATION_STORAGE, REWARD_REGISTRATION_TTL, rewardReferralCode } from '~/utils/rewardRegistration'

/** Browser-only intent bridges registration and email verification without changing checked auth. */
export function useRewardRegistration() {
  const pending = useState<RewardRegistrationIntent | null>('rewards-registration', () => null)
  const loaded = useState('rewards-registration:loaded', () => false)
  function persist(intent: RewardRegistrationIntent | null) {
    pending.value = intent
    if (import.meta.server)
      return
    try {
      if (intent)
        window.localStorage.setItem(REWARD_REGISTRATION_STORAGE, JSON.stringify(intent))
      else
        window.localStorage.removeItem(REWARD_REGISTRATION_STORAGE)
    }
    catch {
      // The current tab still keeps the explicit choice if storage is unavailable.
    }
  }
  function load() {
    if (import.meta.server || loaded.value)
      return
    try {
      pending.value = parseRewardRegistration(window.localStorage.getItem(REWARD_REGISTRATION_STORAGE))
      if (!pending.value)
        window.localStorage.removeItem(REWARD_REGISTRATION_STORAGE)
    }
    catch {
      pending.value = null
    }
    loaded.value = true
  }
  function rememberReferral(value: string) {
    const referralCode = rewardReferralCode(value)
    if (referralCode)
      persist({ referralCode, expiresAt: Date.now() + REWARD_REGISTRATION_TTL })
  }
  function saveConsent(email: string, termsVersion: string, code: string) {
    persist({
      referralCode: rewardReferralCode(code),
      expiresAt: Date.now() + REWARD_REGISTRATION_TTL,
      consent: { email: email.trim().toLowerCase(), termsVersion },
    })
  }
  function clearConsent() {
    const value = pending.value
    persist(value?.referralCode ? { referralCode: value.referralCode, expiresAt: value.expiresAt } : null)
  }
  return { pending, loaded, load, rememberReferral, saveConsent, clearConsent, clear: () => persist(null) }
}
